'use client';

import React, { useState } from 'react';
import { CheckCircle2, Clock, Mail, MapPin, MessageCircle, Phone, Send } from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { resolveContactField, useStorefrontData } from '@/lib/storefront/storefront-data';
import { useLeadSubmit } from '@/lib/storefront/use-lead-form';
import { containerClass, sectionStyle } from './HeaderSections';

interface ContactFormProps {
  source: string;
  formTitle: string;
  formNote: string;
  submitText: string;
  successMessage: string;
}

function ContactForm({
  source,
  formTitle,
  formNote,
  submitText,
  successMessage,
}: ContactFormProps) {
  const { preview, pending, done, error, submit } = useLeadSubmit('contact', source);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await submit({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      message: message.trim(),
      website,
    });
  };

  if (done) {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
        <CheckCircle2 className="h-9 w-9 text-emerald-600" />
        <p className="mt-3 max-w-sm text-sm font-semibold text-emerald-900">{successMessage}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {formTitle && <h3 className="text-lg font-semibold text-slate-900">{formTitle}</h3>}
      {formNote && <p className="text-sm text-slate-500">{formNote}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-medium text-slate-700">
          Full name
          <input
            type="text"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
            placeholder="Youssef El Amrani"
          />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          Email
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
            placeholder="you@example.com"
          />
        </label>
      </div>

      <label className="block text-sm font-medium text-slate-700">
        Phone <span className="font-normal text-slate-400">(optional)</span>
        <input
          type="tel"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
          placeholder="+212 6 00 00 00 00"
        />
      </label>

      <label className="block text-sm font-medium text-slate-700">
        Message
        <textarea
          rows={4}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          className="mt-1.5 w-full resize-y rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
          placeholder="Tell us what you need — dates, a car category, or a question."
        />
      </label>

      {/* Honeypot: real visitors never fill this hidden field. */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(event) => setWebsite(event.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
      {preview && (
        <p className="text-xs text-slate-400">
          Preview mode: this form submits on the live site.
        </p>
      )}

      <button
        type="submit"
        disabled={pending}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Send className="h-4 w-4" />
        {pending ? 'Sending…' : submitText}
      </button>
    </form>
  );
}

interface ContactDetailsProps {
  phone?: string;
  email?: string;
  whatsapp?: string;
  address?: string;
  hours?: string;
  showPhone?: boolean;
  showEmail?: boolean;
  showWhatsapp?: boolean;
  showAddress?: boolean;
  dark?: boolean;
}

function ContactDetails({
  phone = '',
  email = '',
  whatsapp = '',
  address = '',
  hours = '',
  showPhone = true,
  showEmail = true,
  showWhatsapp = true,
  showAddress = true,
  dark = false,
}: ContactDetailsProps) {
  const label = dark ? 'text-slate-500' : 'text-slate-400';
  const value = dark ? 'text-white' : 'text-slate-800';
  const iconBox = dark ? 'bg-white/10 text-brand' : 'bg-brand/10 text-brand';
  const row = `flex items-start gap-3 rounded-xl p-3 transition ${dark ? 'hover:bg-white/5' : 'hover:bg-white'}`;

  return (
    <div className="space-y-1">
      {showPhone && phone && (
        <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className={row}>
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBox}`}>
            <Phone className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${label}`}>Phone</span>
            <span className={`block text-sm font-medium ${value}`}>{phone}</span>
          </span>
        </a>
      )}

      {showEmail && email && (
        <a href={`mailto:${email}`} className={row}>
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBox}`}>
            <Mail className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${label}`}>Email</span>
            <span className={`block break-all text-sm font-medium ${value}`}>{email}</span>
          </span>
        </a>
      )}

      {showWhatsapp && whatsapp && (
        <a
          href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`}
          target="_blank"
          rel="noreferrer"
          className={row}
        >
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBox}`}>
            <MessageCircle className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${label}`}>WhatsApp</span>
            <span className={`block text-sm font-medium ${value}`}>{whatsapp}</span>
          </span>
        </a>
      )}

      {showAddress && address && (
        <div className={row}>
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBox}`}>
            <MapPin className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${label}`}>Address</span>
            <span className={`block whitespace-pre-line text-sm font-medium ${value}`}>{address}</span>
          </span>
        </div>
      )}

      {hours && (
        <div className={row}>
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBox}`}>
            <Clock className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${label}`}>Opening hours</span>
            <span className={`block whitespace-pre-line text-sm font-medium ${value}`}>{hours}</span>
          </span>
        </div>
      )}
    </div>
  );
}

export function ContactSplit({ content, style }: SectionProps) {
  const { agency } = useStorefrontData();
  const {
    eyebrow = 'Contact',
    headline = 'Talk to a human',
    subheadline = 'Questions about a car, a booking or a long-term rental? Send a message and we will get back to you.',
    formTitle = 'Send a message',
    formNote = 'We reply within one business day.',
    submitText = 'Send message',
    successMessage = 'Thanks — your message is on its way. We will reply shortly.',
    showPhone = true,
    showEmail = true,
    showWhatsapp = true,
    showAddress = true,
    source = 'website',
  } = content;
  const phone = resolveContactField(content.phone, agency?.phone);
  const email = resolveContactField(content.email, agency?.email);
  const whatsapp = resolveContactField(content.whatsapp, agency?.whatsapp);
  const address = resolveContactField(content.address, agency?.address);
  const hours = resolveContactField(content.hours, agency?.hours);

  return (
    <section className="w-full bg-slate-50" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div className="rounded-3xl bg-slate-900 p-7 text-white sm:p-9">
            {eyebrow && (
              <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
            )}
            <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">{headline}</h2>
            {subheadline && <p className="mt-3 text-sm leading-relaxed text-slate-300">{subheadline}</p>}
            <div className="mt-7">
              <ContactDetails
                phone={phone}
                email={email}
                whatsapp={whatsapp}
                address={address}
                hours={hours}
                showPhone={Boolean(showPhone)}
                showEmail={Boolean(showEmail)}
                showWhatsapp={Boolean(showWhatsapp)}
                showAddress={Boolean(showAddress)}
                dark
              />
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <ContactForm
              source={String(source)}
              formTitle={formTitle}
              formNote={formNote}
              submitText={submitText}
              successMessage={successMessage}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export function ContactCard({ content, style }: SectionProps) {
  const { agency } = useStorefrontData();
  const {
    eyebrow = 'Contact',
    headline = 'Talk to a human',
    subheadline = 'Questions about a car, a booking or a long-term rental? Send a message and we will get back to you.',
    formTitle = 'Send a message',
    formNote = 'We reply within one business day.',
    submitText = 'Send message',
    successMessage = 'Thanks — your message is on its way. We will reply shortly.',
    showPhone = true,
    showEmail = true,
    showWhatsapp = true,
    showAddress = true,
    source = 'website',
  } = content;
  const phone = resolveContactField(content.phone, agency?.phone);
  const email = resolveContactField(content.email, agency?.email);
  const whatsapp = resolveContactField(content.whatsapp, agency?.whatsapp);
  const address = resolveContactField(content.address, agency?.address);
  const hours = resolveContactField(content.hours, agency?.hours);

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-3xl text-center">
          {eyebrow && (
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
          )}
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
        </div>

        <div className="mx-auto mt-8 grid max-w-5xl gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
            <ContactDetails
              phone={phone}
              email={email}
              whatsapp={whatsapp}
              address={address}
              hours={hours}
              showPhone={Boolean(showPhone)}
              showEmail={Boolean(showEmail)}
              showWhatsapp={Boolean(showWhatsapp)}
              showAddress={Boolean(showAddress)}
            />
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <ContactForm
              source={String(source)}
              formTitle={formTitle}
              formNote={formNote}
              submitText={submitText}
              successMessage={successMessage}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export const CONTACT_VARIATIONS = {
  split: ContactSplit,
  card: ContactCard,
} as const;
